import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Typography, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Chip, CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockCategoryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EnhancedPagination from '../../components/common/EnhancedPagination';
import StockCategoryFormDialog from './StockCategoryFormDialog';

const StockCategories = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // ── Permissions ───────────────────────────────────────────────────────────
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'stockCategories' && p.operations.includes('view'));
    const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'stockCategories' && p.operations.includes('create'));
    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'stockCategories' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'stockCategories' && p.operations.includes('delete'));

    // ── State ─────────────────────────────────────────────────────────────────
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [privilegeError, setPrivilegeError] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({
        page: 1, limit: 10, total: 0, pages: 1
    });

    // ── Dialog state ──────────────────────────────────────────────────────────
    const [formOpen, setFormOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchCategories = useCallback(async () => {
        setLoading(true);
        setPrivilegeError(false);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'name',
                sortOrder: 'asc',
            };
            if (search.trim()) params.search = search.trim();

            const res = await stockCategoryAPI.getAll(params);
            setCategories(res.data?.data || []);
            setPagination(prev => ({
                ...prev,
                total: res.data?.pagination?.total ?? 0,
                pages: res.data?.pagination?.pages ?? 1,
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                setPrivilegeError(true);
            } else {
                toast.error(t('stockCategories.fetchError'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, t]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [search]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpenCreate = () => { setSelectedCategory(null); setFormOpen(true); };
    const handleOpenEdit = (cat) => { setSelectedCategory(cat); setFormOpen(true); };
    const handleFormClose = () => { setFormOpen(false); setSelectedCategory(null); };
    const handleFormSuccess = () => { handleFormClose(); fetchCategories(); };

    const handleDeleteClick = (cat) => { setCategoryToDelete(cat); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!categoryToDelete) return;
        setDeleteLoading(true);
        try {
            await stockCategoryAPI.delete(categoryToDelete._id);
            toast.success(t('stockCategories.deleteSuccess'));
            setDeleteDialogOpen(false);
            setCategoryToDelete(null);
            fetchCategories();
        } catch (err) {
            toast.error(err.response?.data?.message || t('stockCategories.deleteError'));
        } finally {
            setDeleteLoading(false);
        }
    };

    // ── Privilege gate ────────────────────────────────────────────────────────
    if (!canView || privilegeError) {
        return (
            <NoPrivilege
                message={t('stockCategories.noPrivilegeError', 'You do not have permission to view stock categories.')}
            />
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>

            {/* ── Header ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    {t('stockCategories.title')}
                </Typography>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        {t('stockCategories.addButton')}
                    </Button>
                )}
            </Box>

            {/* ── Search ── */}
            <Box mb={2}>
                <TextField
                    size="small"
                    placeholder={t('stockCategories.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 320 }}
                />
            </Box>

            {/* ── Table ── */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>{t('stockCategories.name')}</strong></TableCell>
                            <TableCell><strong>{t('common.description')}</strong></TableCell>
                            <TableCell><strong>{t('common.status')}</strong></TableCell>
                            <TableCell><strong>{t('common.createdAt')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {t('stockCategories.noItemsFound')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : categories.map(cat => (
                            <TableRow key={cat._id} hover>
                                <TableCell>
                                    <Typography fontWeight={500}>{cat.name}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        color="text.secondary"
                                        sx={{
                                            maxWidth: 300,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {cat.description || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={cat.isActive ? t('common.active') : t('common.inactive')}
                                        color={cat.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {cat.createdAt
                                        ? new Date(cat.createdAt).toLocaleDateString()
                                        : '—'}
                                </TableCell>
                                <TableCell align="center">
                                    {canEdit && (
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => handleOpenEdit(cat)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteClick(cat)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── Pagination ── */}
            {!loading && categories.length > 0 && (
                <Box mt={2}>
                    <EnhancedPagination
                        pagination={pagination}
                        onPageChange={p => setPagination(prev => ({ ...prev, page: p }))}
                        onLimitChange={l => setPagination(prev => ({ ...prev, limit: l, page: 1 }))}
                    />
                </Box>
            )}

            {/* ── Dialogs ── */}
            {canAdd && (
                <StockCategoryFormDialog
                    open={formOpen}
                    onClose={handleFormClose}
                    onSuccess={handleFormSuccess}
                    category={selectedCategory}
                />
            )}

            {canDelete && (
                <ConfirmDialog
                    open={deleteDialogOpen}
                    title={t('stockCategories.deleteConfirmTitle')}
                    message={t('stockCategories.deleteConfirmMessage')}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => { setDeleteDialogOpen(false); setCategoryToDelete(null); }}
                    loading={deleteLoading}
                />
            )}

        </Box>
    );
};

export default StockCategories;